import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Archive,
  ChevronRight,
  ExternalLink,
  Lightbulb,
  Pin,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import {
  cardImageAttachment,
  cardSources,
  filterCards,
  metadataFieldDefinitions,
  structuredFields,
} from "../library-model";
import { CARD_LABELS, type CardType, type MemoryCard, type TranscriptChunk, type VideoDetails } from "../types";

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
}

function ErrorBox({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="workspace-error" role="alert">
      <span>Не удалось загрузить данные: {message}</span>
      {retry && <button type="button" onClick={retry}>Повторить</button>}
    </div>
  );
}

function WorkspaceImage({ card, className }: { card: MemoryCard; className: string }) {
  const attachment = cardImageAttachment(card);
  const [failedId, setFailedId] = useState<string | null>(null);
  if (!attachment || failedId === attachment.id) return null;
  return (
    <img
      className={className}
      src={api.fileUrl(attachment.id)}
      alt={card.title}
      loading="lazy"
      onError={() => setFailedId(attachment.id)}
    />
  );
}

function WorkspaceCard({
  card,
  selected,
  search,
}: {
  card: MemoryCard;
  selected: boolean;
  search: string;
}) {
  const target = "/library/" + card.id + (search ? "?" + search : "");
  return (
    <Link className={"workspace-card" + (selected ? " selected" : "")} to={target} aria-current={selected ? "true" : undefined}>
      <WorkspaceImage card={card} className="workspace-card-image" />
      <div className="workspace-card-body">
        <div className="workspace-card-meta">
          <span className={"type-dot type-" + card.type} />
          <span>{CARD_LABELS[card.type]}</span>
          <time>{formatDate(card.created_at)}</time>
        </div>
        <h3>{card.title}</h3>
        <p>{card.summary || card.original_text}</p>
        <div className="workspace-card-tags">
          {card.tags.slice(0, 3).map((tag) => <span key={tag}>#{tag}</span>)}
        </div>
      </div>
      <ChevronRight className="workspace-card-chevron" size={16} aria-hidden="true" />
    </Link>
  );
}

function MetadataEditor({
  card,
  values,
  setValue,
}: {
  card: MemoryCard;
  values: Record<string, string>;
  setValue: (key: string, value: string) => void;
}) {
  return (
    <>
      {metadataFieldDefinitions(card.type).map((field) => (
        <label key={field.key}>
          {field.label}
          {field.multiline ? (
            <textarea
              rows={field.key === "steps" ? 7 : 5}
              value={values[field.key] ?? ""}
              onChange={(event) => setValue(field.key, event.target.value)}
              placeholder={field.list ? "Каждый пункт с новой строки" : undefined}
            />
          ) : (
            <input value={values[field.key] ?? ""} onChange={(event) => setValue(field.key, event.target.value)} />
          )}
        </label>
      ))}
    </>
  );
}

function InlineEditor({
  card,
  onCancel,
  onSaved,
}: {
  card: MemoryCard;
  onCancel: () => void;
  onSaved: (card: MemoryCard) => void;
}) {
  const [title, setTitle] = useState(card.title);
  const [summary, setSummary] = useState(card.summary);
  const [originalText, setOriginalText] = useState(card.original_text);
  const [tags, setTags] = useState(card.tags.join(", "));
  const [type, setType] = useState<CardType>(card.type);
  const [metadataValues, setMetadataValues] = useState<Record<string, string>>(
    () => Object.fromEntries(structuredFields(card).map((field) => [field.key, field.value])),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const metadata = { ...card.metadata };
    for (const field of metadataFieldDefinitions(type)) {
      const value = (metadataValues[field.key] ?? "").trim();
      if (!value) delete metadata[field.key];
      else metadata[field.key] = field.list ? value.split(/\r?\n/).map((item) => item.trim()).filter(Boolean) : value;
    }
    try {
      const result = await api.updateCard(card.id, {
        type,
        title,
        summary,
        originalText,
        tags: tags.split(",").map((item) => item.trim()).filter(Boolean),
        metadata,
      });
      onSaved(result.card);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "UNKNOWN");
      setSaving(false);
    }
  }

  return (
    <form className="workspace-editor" onSubmit={submit}>
      <div className="workspace-editor-heading">
        <strong>Редактирование</strong>
        <button type="button" className="icon-button" onClick={onCancel} aria-label="Закрыть редактирование"><X size={18} /></button>
      </div>
      <label>Тип<select value={type} onChange={(event) => setType(event.target.value as CardType)}>{Object.entries(CARD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Заголовок<input required maxLength={240} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
      <MetadataEditor card={{ ...card, type }} values={metadataValues} setValue={(key, value) => setMetadataValues((current) => ({ ...current, [key]: value }))} />
      <label>Краткое содержание<textarea rows={4} value={summary} onChange={(event) => setSummary(event.target.value)} /></label>
      <label>Заметка<textarea rows={9} value={originalText} onChange={(event) => setOriginalText(event.target.value)} /></label>
      <label>Теги<input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="через запятую" /></label>
      {error && <ErrorBox message={error} />}
      <div className="workspace-editor-actions">
        <button type="button" className="secondary-button" onClick={onCancel}>Отмена</button>
        <button className="primary-button" disabled={saving}>{saving ? "Сохраняю…" : "Сохранить"}</button>
      </div>
    </form>
  );
}

function VideoTranscriptPanel({ card }: { card: MemoryCard }) {
  const [details, setDetails] = useState<VideoDetails | null>(null);
  const [chunks, setChunks] = useState<TranscriptChunk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.videoTranscript(card.id)
      .then((result) => {
        if (!active) return;
        setDetails(result.details);
        setChunks(result.chunks);
        setError(null);
      })
      .catch((cause: Error) => { if (active) setError(cause.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [card.id]);

  async function toggleWatched() {
    if (!details) return;
    const status = details.watch_status === "watched" ? "to_watch" : "watched";
    await api.setVideoStatus(card.id, status);
    setDetails({ ...details, watch_status: status });
  }

  const timestamps = Array.isArray(card.metadata.timestamps)
    ? card.metadata.timestamps.filter((item): item is { seconds: number; title: string } =>
        Boolean(item) && typeof item === "object" && "seconds" in item && typeof item.seconds === "number"
        && "title" in item && typeof item.title === "string")
    : [];
  const baseUrl = card.source_url ?? "";

  return (
    <>
      <section className="workspace-section">
        <h2>Просмотр</h2>
        <button type="button" className="secondary-button" disabled={!details} onClick={() => void toggleWatched()}>
          {details?.watch_status === "watched" ? "Вернуть в «Посмотреть»" : "Отметить просмотренным"}
        </button>
      </section>
      {timestamps.length > 0 && (
        <section className="workspace-section workspace-sources">
          <h2>Временные метки</h2>
          {timestamps.map((item) => (
            <a key={item.seconds + item.title} href={`${baseUrl}&t=${item.seconds}s`} target="_blank" rel="noreferrer">
              <span>{Math.floor(item.seconds / 60)}:{String(item.seconds % 60).padStart(2, "0")} · {item.title}</span>
              <ExternalLink size={14} />
            </a>
          ))}
        </section>
      )}
      <section className="workspace-section">
        <h2>Полный транскрипт</h2>
        {loading && <p>Загружаю транскрипт…</p>}
        {error && <ErrorBox message={error} />}
        {!loading && !error && chunks.length === 0 && (
          <p>{details?.transcript_status === "approval_required"
            ? "Для этого длинного видео нужно подтвердить полное распознавание в Telegram."
            : details?.transcript_status === "failed"
              ? "Транскрипт пока недоступен."
              : "Видео ещё обрабатывается."}</p>
        )}
        {chunks.length > 0 && (
          <details>
            <summary>Показать весь текст ({chunks.length} фрагм.)</summary>
            <div className="preserve-lines">
              {chunks.map((chunk, index) => (
                <p key={index}>
                  {chunk.startMs === null ? "" : `[${Math.floor(chunk.startMs / 60_000)}:${String(Math.floor(chunk.startMs / 1000) % 60).padStart(2, "0")}] `}
                  {chunk.text}
                </p>
              ))}
            </div>
          </details>
        )}
      </section>
    </>
  );
}

function WorkspaceDetail({
  card,
  loading,
  error,
  listSearch,
  onRetry,
  onChanged,
}: {
  card: MemoryCard | null;
  loading: boolean;
  error: string | null;
  listSearch: string;
  onRetry: () => void;
  onChanged: (card: MemoryCard | null) => void;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const closeTarget = "/library" + (listSearch ? "?" + listSearch : "");

  if (loading) return <div className="workspace-detail-state"><span className="spinner" />Открываю запись…</div>;
  if (error) return <div className="workspace-detail-state"><ErrorBox message={error} retry={onRetry} /></div>;
  if (!card) {
    return (
      <div className="workspace-detail-state workspace-detail-empty">
        <span><Lightbulb /></span>
        <h2>Выберите запись</h2>
        <p>Здесь появятся содержание, изображения, поля и источники карточки.</p>
      </div>
    );
  }
  const currentCard = card;

  async function patch(input: Record<string, unknown>) {
    setActionError(null);
    try {
      const result = await api.updateCard(currentCard.id, input);
      onChanged(result.card);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "UNKNOWN");
    }
  }

  async function remove() {
    setActionError(null);
    try {
      await api.deleteCard(currentCard.id);
      onChanged(null);
      navigate(closeTarget);
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "UNKNOWN");
    }
  }

  if (editing) {
    return <InlineEditor card={card} onCancel={() => setEditing(false)} onSaved={(next) => { onChanged(next); setEditing(false); }} />;
  }

  const fields = structuredFields(card);
  const sources = cardSources(card);
  const pinned = card.metadata.pinned === true;
  return (
    <article className="workspace-detail">
      <header className="workspace-detail-toolbar">
        <Link className="workspace-mobile-back" to={closeTarget}>← К списку</Link>
        <span className="workspace-save-state">Сохранено</span>
        <div>
          <button type="button" className={"icon-button" + (pinned ? " active" : "")} onClick={() => void patch({ metadata: { ...card.metadata, pinned: !pinned } })} aria-label={pinned ? "Открепить" : "Закрепить"}><Pin size={17} /></button>
          <button type="button" className="icon-button" onClick={() => void patch({ archived: !card.archived_at })} aria-label={card.archived_at ? "Вернуть из архива" : "В архив"}><Archive size={17} /></button>
          <button type="button" className="icon-button danger-icon" onClick={() => void remove()} aria-label="В корзину"><Trash2 size={17} /></button>
        </div>
      </header>
      <div className="workspace-detail-scroll">
        <WorkspaceImage card={card} className="workspace-detail-cover" />
        <div className="workspace-detail-title">
          <span className="type-pill">{CARD_LABELS[card.type]}</span>
          <h1>{card.title}</h1>
          <button type="button" className="secondary-button" onClick={() => setEditing(true)}>Изменить</button>
        </div>
        {fields.length > 0 && (
          <dl className="workspace-fields">
            {fields.map((field) => <div key={field.key}><dt>{field.label}</dt><dd className="preserve-lines">{field.value}</dd></div>)}
          </dl>
        )}
        {card.summary && <section className="workspace-section"><h2>Краткое содержание</h2><p className="preserve-lines">{card.summary}</p></section>}
        {card.type === "video" && <VideoTranscriptPanel card={card} />}
        <section className="workspace-section">
          <h2>Заметка</h2>
          <p className="preserve-lines">{card.original_text || "Исходная заметка отсутствует."}</p>
        </section>
        {card.tags.length > 0 && <div className="tags large">{card.tags.map((tag) => <span key={tag}>#{tag}</span>)}</div>}
        {sources.length > 0 && (
          <section className="workspace-section workspace-sources">
            <h2>Источники</h2>
            {sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer"><span>{source.title}</span><ExternalLink size={14} /></a>)}
          </section>
        )}
        {actionError && <ErrorBox message={actionError} />}
      </div>
    </article>
  );
}

function WorkspaceComposer({
  initialType,
  onClose,
  onCreated,
}: {
  initialType?: CardType;
  onClose: () => void;
  onCreated: (card: MemoryCard) => void;
}) {
  const [type, setType] = useState<CardType>(initialType ?? "note");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await api.createCard({
        type,
        title,
        originalText: text,
        summary: text.slice(0, 500),
        sourceKind: "web",
        tags: tags.split(",").map((item) => item.trim()).filter(Boolean),
        entities: [],
        metadata: {},
      });
      onCreated(result.card);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "UNKNOWN");
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <form className="modal" onSubmit={submit}>
        <div className="modal-title"><div><span className="eyebrow">Новая запись</span><h2>Сохранить мысль</h2></div><button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><X /></button></div>
        <label>Тип<select value={type} onChange={(event) => setType(event.target.value as CardType)}>{Object.entries(CARD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label>Заголовок<input required maxLength={240} value={title} onChange={(event) => setTitle(event.target.value)} /></label>
        <label>Содержание<textarea required rows={8} maxLength={20000} value={text} onChange={(event) => setText(event.target.value)} /></label>
        <label>Теги<input value={tags} onChange={(event) => setTags(event.target.value)} placeholder="через запятую" /></label>
        {error && <ErrorBox message={error} />}
        <button className="primary-button" disabled={saving}>{saving ? "Сохраняю…" : "Сохранить"}</button>
      </form>
    </div>
  );
}

export default function LibraryWorkspace() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeValue = searchParams.get("type") ?? "";
  const type = Object.hasOwn(CARD_LABELS, typeValue) ? typeValue as CardType : undefined;
  const query = searchParams.get("q") ?? "";
  const tag = searchParams.get("tag") ?? "";
  const archived = searchParams.get("archived") === "true";
  const pinnedOnly = searchParams.get("pinned") === "true";
  const [cards, setCards] = useState<MemoryCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<MemoryCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [composer, setComposer] = useState(false);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const result = await api.listCards({ type, archived: String(archived), limit: "100" });
      setCards(result.cards);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "UNKNOWN");
    } finally {
      if (!background) setLoading(false);
    }
  }, [archived, type]);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => { void load(true); }, 10_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const loadDetail = useCallback(async () => {
    if (!id) {
      setSelectedCard(null);
      setDetailError(null);
      return;
    }
    const fromList = cards.find((card) => card.id === id);
    if (fromList) {
      setSelectedCard(fromList);
      setDetailError(null);
      return;
    }
    setDetailLoading(true);
    try {
      const result = await api.getCard(id);
      setSelectedCard(result.card);
      setDetailError(null);
    } catch (cause) {
      setDetailError(cause instanceof Error ? cause.message : "UNKNOWN");
    } finally {
      setDetailLoading(false);
    }
  }, [cards, id]);

  useEffect(() => { void loadDetail(); }, [loadDetail]);

  const visible = useMemo(() => {
    const filtered = filterCards(cards, query, tag);
    return pinnedOnly ? filtered.filter((card) => card.metadata.pinned === true) : filtered;
  }, [cards, pinnedOnly, query, tag]);
  const listSearch = searchParams.toString();
  const title = archived ? "Архив" : pinnedOnly ? "Закреплённые" : type ? CARD_LABELS[type] : tag ? "#" + tag : "Все записи";

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function applyChangedCard(card: MemoryCard | null) {
    setSelectedCard(card);
    if (!card) {
      setCards((current) => current.filter((item) => item.id !== id));
      return;
    }
    if (Boolean(card.archived_at) !== archived || (pinnedOnly && card.metadata.pinned !== true)) {
      setCards((current) => current.filter((item) => item.id !== card.id));
      setSelectedCard(null);
      navigate("/library" + (listSearch ? "?" + listSearch : ""));
      return;
    }
    setCards((current) => current.map((item) => item.id === card.id ? card : item));
  }

  return (
    <section className={"library-workspace" + (id ? " has-selection" : "")}>
      <div className="library-list-pane">
        <header className="library-list-header">
          <div><span className="eyebrow">Библиотека</span><h1>{title}</h1><small>{visible.length} записей</small></div>
          <button type="button" className="primary-button compact" onClick={() => setComposer(true)}><Plus size={17} />Новая запись</button>
        </header>
        <label className="workspace-search"><Search size={17} /><input value={query} onChange={(event) => setFilter("q", event.target.value)} placeholder="Искать в Idea-bot" /></label>
        {error && <ErrorBox message={error} retry={() => void load()} />}
        <div className="workspace-card-list" aria-label="Карточки памяти">
          {loading ? <div className="workspace-list-state"><span className="spinner" />Загружаю…</div> : visible.map((card) => <WorkspaceCard key={card.id} card={card} selected={card.id === id} search={listSearch} />)}
          {!loading && !error && visible.length === 0 && <div className="workspace-list-state"><Lightbulb /><strong>Записей пока нет</strong><span>Измените фильтр или создайте новую карточку.</span></div>}
        </div>
      </div>
      <div className="library-detail-pane">
        <WorkspaceDetail
          key={id || "empty"}
          card={selectedCard}
          loading={detailLoading}
          error={detailError}
          listSearch={listSearch}
          onRetry={() => void loadDetail()}
          onChanged={applyChangedCard}
        />
      </div>
      {composer && <WorkspaceComposer initialType={type} onClose={() => setComposer(false)} onCreated={(card) => { setComposer(false); setCards((current) => [card, ...current]); navigate("/library/" + card.id + (listSearch ? "?" + listSearch : "")); }} />}
    </section>
  );
}
